import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  Plus, Calendar, Users, MapPin, ArrowRight, Sparkles, Flame, Trophy, Zap, Wallet as WalletIcon,
} from "lucide-react";
import { format, isToday, isTomorrow } from "date-fns";
import GuidedTour from "@/components/GuidedTour";
import BentoCard from "@/components/bento/BentoCard";
import ReliabilityRing from "@/components/bento/ReliabilityRing";
import RibbonSpinner from "@/components/brand/RibbonSpinner";
import { cn } from "@/lib/utils";

interface KrewWithMembers {
  id: string; title: string; event_date: string; event_time: string; status: string;
  city: string; area: string | null; budget_per_person: number; vibe: string[] | null;
  invite_code: string; creator_id: string; member_count?: number;
}

const statusTone: Record<string, string> = {
  planning: "text-krew-cyan",
  committed: "text-krew-mint",
  venue_voting: "text-krew-violet",
  booked: "text-krew-lime",
  active: "text-krew-mint",
  completed: "text-muted-foreground",
  cancelled: "text-destructive",
};

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Afternoon";
  if (h < 21) return "Evening";
  return "Night owl mode";
};

const Dashboard = () => {
  const { user, profile } = useAuth();
  const [krews, setKrews] = useState<KrewWithMembers[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    const fetchKrews = async () => {
      const { data: memberKrewIds } = await supabase
        .from("krew_members").select("krew_id").eq("user_id", user.id);
      const krewIds = memberKrewIds?.map(m => m.krew_id) || [];
      const { data: createdKrews } = await supabase
        .from("krews").select("id").eq("creator_id", user.id);
      const allIds = [...new Set([...krewIds, ...(createdKrews?.map(k => k.id) || [])])];
      if (allIds.length === 0) { setLoading(false); return; }
      const { data } = await supabase
        .from("krews").select("*").in("id", allIds).order("event_date", { ascending: true });
      if (data) {
        const krewsWithCounts = await Promise.all(
          data.map(async (krew) => {
            const { count } = await supabase.from("krew_members").select("*", { count: "exact", head: true }).eq("krew_id", krew.id);
            return { ...krew, member_count: count || 0 } as KrewWithMembers;
          }),
        );
        setKrews(krewsWithCounts);
      }
      setLoading(false);
    };
    fetchKrews();
  }, [user]);

  const activeKrews = krews.filter((k) => !["completed", "cancelled"].includes(k.status));
  const pastKrews = krews.filter((k) => ["completed", "cancelled"].includes(k.status));
  const tonightKrew = activeKrews.find(k => isToday(new Date(k.event_date)));
  const tomorrowKrew = !tonightKrew ? activeKrews.find(k => isTomorrow(new Date(k.event_date))) : null;
  const urgentKrew = tonightKrew || tomorrowKrew;

  const reliability = profile?.reliability_score ?? 100;
  const attended = profile?.total_krews_attended ?? 0;
  const streak = Math.min(attended, 12); // simple visual streak

  return (
    <div className="space-y-6 py-2">
      <GuidedTour />

      {/* Editorial header */}
      <header className="flex flex-wrap items-end justify-between gap-3 pt-2">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
            {format(new Date(), "EEE · MMM d")} · {getGreeting()}
          </p>
          <h1 className="mt-1 font-display text-4xl font-medium leading-[1.05] tracking-tight sm:text-5xl">
            Hey <span className="text-gradient-ribbon animate-ribbon-shift">{profile?.display_name?.split(" ")[0] || "you"}</span>,
            <br />
            <span className="text-muted-foreground">
              {tonightKrew ? "tonight's the night." : activeKrews.length ? "the squad's plotting." : "let's plot a night."}
            </span>
          </h1>
        </div>
        <Link
          to="/create-krew"
          data-tour="create-krew"
          className="ribbon-sweep inline-flex items-center gap-2 rounded-pill bg-ribbon bg-[length:200%_200%] animate-ribbon-shift px-5 py-3 text-sm font-semibold text-krew-ink shadow-ribbon"
        >
          <Plus className="h-4 w-4" /> Start a Krew
        </Link>
      </header>

      {/* BENTO GRID */}
      <section className="grid auto-rows-[minmax(120px,auto)] grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-6">
        {/* TONIGHT / NEXT — hero tile */}
        <BentoCard accent="ribbon" interactive className="col-span-2 row-span-2 min-h-[260px] lg:col-span-4">
          {urgentKrew ? (
            <Link to={`/krew/${urgentKrew.id}`} className="flex h-full flex-col justify-between p-6">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-pill bg-background/40 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-krew-cyan backdrop-blur">
                  <Sparkles className="h-3 w-3" /> {tonightKrew ? "Tonight" : "Tomorrow"}
                </span>
                <span className={cn("font-mono text-[10px] uppercase tracking-[0.18em]", statusTone[urgentKrew.status])}>
                  · {urgentKrew.status.replace("_", " ")}
                </span>
              </div>
              <div>
                <h2 className="font-display text-3xl font-medium tracking-tight sm:text-4xl">
                  {urgentKrew.title}
                </h2>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {format(new Date(urgentKrew.event_date), "EEE, MMM d")} · {urgentKrew.event_time?.slice(0,5)}</span>
                  <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {urgentKrew.area || urgentKrew.city}</span>
                  <span className="inline-flex items-center gap-1.5"><Users className="h-4 w-4" /> {urgentKrew.member_count} in</span>
                </div>
              </div>
            </Link>
          ) : (
            <Link to="/create-krew" className="flex h-full flex-col items-start justify-between p-6">
              <span className="inline-flex items-center gap-1.5 rounded-pill bg-background/40 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground backdrop-blur">
                <Sparkles className="h-3 w-3" /> No plans yet
              </span>
              <div>
                <h2 className="font-display text-3xl font-medium tracking-tight sm:text-4xl">
                  Your night is a <span className="text-gradient-ribbon animate-ribbon-shift">blank canvas</span>.
                </h2>
                <p className="mt-2 max-w-md text-sm text-muted-foreground">Pick a vibe, drop a date, the Krew assembles itself.</p>
              </div>
              <span className="inline-flex items-center gap-1 text-sm font-medium text-krew-mint">
                Paint it <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          )}
        </BentoCard>

        {/* RELIABILITY RING */}
        <BentoCard accent="cyan" className="col-span-1 row-span-1 lg:col-span-2 lg:row-span-1">
          <Link to="/profile" className="flex h-full items-center gap-4 p-5">
            <ReliabilityRing value={reliability} label="rel" />
            <div className="min-w-0">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Reliability</p>
              <p className="font-display text-2xl font-medium leading-tight">
                {reliability >= 90 ? "Iron-clad" : reliability >= 75 ? "Solid" : reliability >= 50 ? "Building" : "Flaky"}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">{attended} nights attended</p>
            </div>
          </Link>
        </BentoCard>

        {/* STREAK */}
        <BentoCard accent="sunburst" className="col-span-1 row-span-1 lg:col-span-2">
          <div className="flex h-full items-center justify-between gap-3 p-5">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Streak</p>
              <p className="font-display text-4xl font-medium leading-none text-foreground">
                <span className="font-mono tabular">{streak}</span>
              </p>
              <p className="mt-1 text-xs text-muted-foreground">{streak ? "nights in a row" : "go light one up"}</p>
            </div>
            <Flame className="h-10 w-10 text-krew-sunburst drop-shadow-[0_0_18px_hsl(var(--krew-sunburst)/0.7)]" />
          </div>
        </BentoCard>

        {/* QUICK CREATE */}
        <BentoCard accent="magenta" interactive className="col-span-2 lg:col-span-2">
          <Link to="/create-krew" className="flex h-full items-center gap-4 p-5">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-ribbon bg-[length:200%_200%] animate-ribbon-shift shadow-ribbon">
              <Plus className="h-6 w-6 text-krew-ink" strokeWidth={2.5} />
            </span>
            <div>
              <p className="font-display text-xl font-medium leading-tight">New plan</p>
              <p className="text-xs text-muted-foreground">Pull the squad together in 60s</p>
            </div>
            <ArrowRight className="ml-auto h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1" />
          </Link>
        </BentoCard>

        {/* WALLET */}
        <BentoCard accent="mint" interactive className="col-span-1 lg:col-span-2">
          <Link to="/wallet" className="flex h-full flex-col justify-between p-5">
            <div className="flex items-center justify-between">
              <WalletIcon className="h-5 w-5 text-krew-mint" />
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Wallet</span>
            </div>
            <div>
              <p className="font-display text-3xl font-medium leading-none">Group pool</p>
              <p className="mt-1 text-xs text-muted-foreground">Deposits, splits, settlements</p>
            </div>
          </Link>
        </BentoCard>

        {/* CITY RANK */}
        <BentoCard accent="violet" interactive className="col-span-1 lg:col-span-2">
          <Link to="/leaderboard" className="flex h-full flex-col justify-between p-5">
            <div className="flex items-center justify-between">
              <Trophy className="h-5 w-5 text-krew-violet" />
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">City rank</span>
            </div>
            <div>
              <p className="font-display text-3xl font-medium leading-none">Climb the boards</p>
              <p className="mt-1 text-xs text-muted-foreground">{profile?.city ?? "Your city"} · top movers tonight</p>
            </div>
          </Link>
        </BentoCard>

        {/* VENUES SHORTCUT */}
        <BentoCard accent="lime" interactive className="col-span-2 lg:col-span-2">
          <Link to="/venues" className="flex h-full items-center gap-4 p-5">
            <MapPin className="h-5 w-5 text-krew-lime" />
            <div>
              <p className="font-display text-xl font-medium leading-tight">Discover venues</p>
              <p className="text-xs text-muted-foreground">100+ spots, AI-ranked for your vibe</p>
            </div>
            <ArrowRight className="ml-auto h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1" />
          </Link>
        </BentoCard>
      </section>

      {/* Upcoming krews — horizontal scroll */}
      <section className="space-y-3">
        <div className="flex items-baseline justify-between">
          <h2 className="font-display text-2xl font-medium tracking-tight">Your krews</h2>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            {activeKrews.length} active · {pastKrews.length} past
          </span>
        </div>

        {loading ? (
          <div className="flex h-40 items-center justify-center">
            <RibbonSpinner size={32} />
          </div>
        ) : activeKrews.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-3xl border border-dashed border-foreground/10 bg-glass p-10 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-ribbon-soft">
              <Zap className="h-6 w-6 text-krew-cyan" />
            </div>
            <h3 className="mb-2 font-display text-xl">Nothing on the calendar.</h3>
            <p className="mx-auto mb-5 max-w-sm text-sm text-muted-foreground">
              Every iconic night started with someone hitting "new plan" first. Be that someone.
            </p>
            <Link to="/create-krew" className="ribbon-sweep inline-flex items-center gap-2 rounded-pill bg-ribbon bg-[length:200%_200%] animate-ribbon-shift px-5 py-2.5 text-sm font-semibold text-krew-ink shadow-ribbon">
              Start a Krew <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        ) : (
          <div className="-mx-4 flex gap-3 overflow-x-auto px-4 pb-2 scrollbar-none sm:mx-0 sm:px-0">
            {activeKrews.map((krew, i) => (
              <motion.div
                key={krew.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="min-w-[280px] flex-1 sm:min-w-[300px] sm:max-w-[360px]"
              >
                <Link to={`/krew/${krew.id}`} className="group block h-full rounded-3xl bg-glass p-5 transition-all hover:shadow-ribbon">
                  <div className="mb-3 flex items-center justify-between">
                    <span className={cn("font-mono text-[10px] uppercase tracking-[0.18em]", statusTone[krew.status] || "text-muted-foreground")}>
                      {krew.status.replace("_", " ")}
                    </span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1" />
                  </div>
                  <h3 className="font-display text-xl font-medium leading-tight">{krew.title}</h3>
                  <div className="mt-3 space-y-1.5 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2"><Calendar className="h-3.5 w-3.5" />{format(new Date(krew.event_date), "EEE, MMM d")} · {krew.event_time?.slice(0,5)}</div>
                    <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5" />{krew.area || krew.city}</div>
                    <div className="flex items-center gap-2"><Users className="h-3.5 w-3.5" />{krew.member_count} member{krew.member_count !== 1 ? "s" : ""}</div>
                  </div>
                  {krew.vibe?.length ? (
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {krew.vibe.slice(0, 3).map((v) => (
                        <span key={v} className="rounded-pill bg-muted/60 px-2 py-0.5 text-[11px] text-muted-foreground">{v}</span>
                      ))}
                    </div>
                  ) : null}
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {pastKrews.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-display text-2xl font-medium tracking-tight">Past nights</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {pastKrews.map((krew) => (
              <Link key={krew.id} to={`/krew/${krew.id}`} className="rounded-2xl bg-card/40 p-4 transition-colors hover:bg-card/70">
                <h3 className="font-medium">{krew.title}</h3>
                <p className="font-mono text-[11px] text-muted-foreground">{format(new Date(krew.event_date), "MMM d, yyyy")}</p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default Dashboard;